class AdminDashboard extends HTMLElement {
  constructor() {
    super();
    this.supabaseUrl = this.dataset.supabaseUrl;
    this.anonKey = this.dataset.supabaseAnonKey;

    this.loginPanel = this.querySelector('.admin-dashboard__login');
    this.loginForm = this.querySelector('.admin-dashboard__login-form');
    this.loginError = this.querySelector('.admin-dashboard__error:not(.admin-dashboard__reset-error)');
    this.forgotBtn = this.querySelector('.admin-dashboard__forgot');
    this.forgotSuccess = this.querySelector('.admin-dashboard__forgot-success');
    this.resetPanel = this.querySelector('.admin-dashboard__reset');
    this.resetForm = this.querySelector('.admin-dashboard__reset-form');
    this.resetError = this.querySelector('.admin-dashboard__reset-error');
    this.panel = this.querySelector('.admin-dashboard__panel');
    this.statsEl = this.querySelector('.admin-dashboard__stats');
    this.tableBody = this.querySelector('.admin-dashboard__table tbody');
    this.emptyEl = this.querySelector('.admin-dashboard__empty');
    this.logoutBtn = this.querySelector('.admin-dashboard__logout');
    this.exportBtn = this.querySelector('.admin-dashboard__export');
    this.filterButtons = this.querySelectorAll('.admin-dashboard__filter-btn[data-range]');

    this.tabButtons = this.querySelectorAll('.admin-dashboard__tab-btn');
    this.tabPanels = this.querySelectorAll('.admin-dashboard__tab-panel');

    this.reportEl = this.querySelector('.admin-dashboard__report');
    this.reportFilterButtons = this.querySelectorAll('[data-report-range]');

    this.costsForm = this.querySelector('.admin-dashboard__add-cost-form');
    this.costsBody = this.querySelector('.admin-dashboard__costs-body');
    this.costsEmpty = this.querySelector('.admin-dashboard__costs-empty');

    this.expensesForm = this.querySelector('.admin-dashboard__add-expense-form');
    this.expensesBody = this.querySelector('.admin-dashboard__expenses-body');
    this.expensesEmpty = this.querySelector('.admin-dashboard__expenses-empty');

    this.subscriptionsBody = this.querySelector('.admin-dashboard__subscriptions-body');
    this.subscriptionsEmpty = this.querySelector('.admin-dashboard__subscriptions-empty');

    this.writerBooksBody = this.querySelector('.admin-dashboard__writer-books-body');
    this.writerBooksEmpty = this.querySelector('.admin-dashboard__writer-books-empty');

    this.promotionBody = this.querySelector('.admin-dashboard__promotion-body');
    this.promotionEmpty = this.querySelector('.admin-dashboard__promotion-empty');

    this.digitalOrdersBody = this.querySelector('.admin-dashboard__digital-orders-body');
    this.digitalOrdersEmpty = this.querySelector('.admin-dashboard__digital-orders-empty');

    this.ebookFilesForm = this.querySelector('.admin-dashboard__add-ebook-file-form');
    this.ebookFilesBody = this.querySelector('.admin-dashboard__ebook-files-body');
    this.ebookFilesEmpty = this.querySelector('.admin-dashboard__ebook-files-empty');
    this.ebookFileMessage = this.querySelector('.admin-dashboard__ebook-file-message');

    this.reviewsBody = this.querySelector('.admin-dashboard__reviews-body');
    this.reviewsEmpty = this.querySelector('.admin-dashboard__reviews-empty');

    this.orders = [];
    this.productCosts = [];
    this.expenses = [];
    this.writerSubmissions = [];
    this.writerBooks = [];
    this.digitalOrders = [];
    this.digitalBooks = [];
    this.reviews = [];
    this.currentRange = 'all';
    this.reportRange = 'all';
    this.accessToken = sessionStorage.getItem('loubna_admin_token');
    this.refreshToken = sessionStorage.getItem('loubna_admin_refresh_token');
    this.tokenExpiresAt = Number(sessionStorage.getItem('loubna_admin_token_expires_at') || 0);
    this.refreshTimer = null;

    this.loginForm.addEventListener('submit', this.handleLogin.bind(this));
    this.logoutBtn.addEventListener('click', this.handleLogout.bind(this));
    this.exportBtn.addEventListener('click', this.exportCsv.bind(this));
    this.forgotBtn.addEventListener('click', this.handleForgotPassword.bind(this));
    this.resetForm.addEventListener('submit', this.handleResetPassword.bind(this));
    this.filterButtons.forEach((btn) =>
      btn.addEventListener('click', () => this.setRange(btn.dataset.range))
    );
    this.tabButtons.forEach((btn) =>
      btn.addEventListener('click', () => this.switchTab(btn.dataset.tab))
    );
    this.reportFilterButtons.forEach((btn) =>
      btn.addEventListener('click', () => this.setReportRange(btn.dataset.reportRange))
    );
    this.costsForm.addEventListener('submit', this.handleAddCost.bind(this));
    this.expensesForm.addEventListener('submit', this.handleAddExpense.bind(this));
    this.ebookFilesForm.addEventListener('submit', this.handleUploadEbookFile.bind(this));

    const recoveryToken = this.getRecoveryTokenFromUrl();
    if (recoveryToken) {
      this.recoveryToken = recoveryToken;
      this.loginPanel.hidden = true;
      this.resetPanel.hidden = false;
    } else if (this.accessToken) {
      if (this.tokenExpiresAt && this.tokenExpiresAt <= Date.now()) {
        // The access token already expired while the tab was closed/idle -
        // try a silent refresh before falling back to the login screen.
        this.refreshSession().then((ok) => {
          if (ok) this.showDashboard();
        });
      } else {
        this.scheduleTokenRefresh();
        this.showDashboard();
      }
    }
  }

  getRecoveryTokenFromUrl() {
    const hash = new URLSearchParams(window.location.hash.replace('#', ''));
    if (hash.get('type') !== 'recovery') return null;
    return hash.get('access_token');
  }

  async handleForgotPassword() {
    this.forgotSuccess.style.display = 'none';
    const email = new FormData(this.loginForm).get('email');

    if (!email) {
      this.loginError.textContent = 'اكتب بريدك الإلكتروني أولاً.';
      this.loginError.style.display = 'block';
      return;
    }

    const redirectTo = window.location.origin + window.location.pathname;

    await fetch(`${this.supabaseUrl}/auth/v1/recover?redirect_to=${encodeURIComponent(redirectTo)}`, {
      method: 'POST',
      headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    this.loginError.style.display = 'none';
    this.forgotSuccess.style.display = 'block';
  }

  async handleResetPassword(event) {
    event.preventDefault();
    this.resetError.style.display = 'none';

    const newPassword = new FormData(this.resetForm).get('new_password');

    try {
      const response = await fetch(`${this.supabaseUrl}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.recoveryToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: newPassword }),
      });

      if (!response.ok) throw new Error('reset failed');

      history.replaceState(null, '', window.location.pathname);
      this.resetPanel.hidden = true;
      this.loginPanel.hidden = false;
      this.forgotSuccess.textContent = 'تم تحديث كلمة المرور، سجّل الدخول الآن.';
      this.forgotSuccess.style.display = 'block';
    } catch (error) {
      this.resetError.style.display = 'block';
    }
  }

  async handleLogin(event) {
    event.preventDefault();
    this.loginError.style.display = 'none';
    this.forgotSuccess.style.display = 'none';

    const formData = new FormData(this.loginForm);
    const email = formData.get('email');
    const password = formData.get('password');

    try {
      const response = await fetch(`${this.supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) throw new Error('invalid credentials');

      const data = await response.json();
      this.storeSession(data);
      this.showDashboard();
    } catch (error) {
      this.loginError.style.display = 'block';
    }
  }

  // Access tokens are short-lived (30 min) on purpose - so a stolen token
  // is only useful for a short window - but that means a real work session
  // needs to renew itself in the background via the refresh token Supabase
  // already issues alongside it, or the merchant would get logged out
  // mid-task every 30 minutes. Called after both a fresh login and a
  // successful refresh, so the next renewal is always scheduled.
  storeSession(data) {
    this.accessToken = data.access_token;
    this.refreshToken = data.refresh_token;
    this.tokenExpiresAt = Date.now() + (Number(data.expires_in) || 1800) * 1000;
    sessionStorage.setItem('loubna_admin_token', this.accessToken);
    sessionStorage.setItem('loubna_admin_refresh_token', this.refreshToken);
    sessionStorage.setItem('loubna_admin_token_expires_at', String(this.tokenExpiresAt));
    this.scheduleTokenRefresh();
  }

  scheduleTokenRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    // Refresh 2 minutes before the access token actually expires, so
    // there's always a valid token ready before any request could hit a
    // stale one - never less than 5s out, in case the clock skew or a
    // slow page load already ate into that buffer.
    const msUntilRefresh = Math.max(this.tokenExpiresAt - Date.now() - 120000, 5000);
    this.refreshTimer = setTimeout(() => this.refreshSession(), msUntilRefresh);
  }

  async refreshSession() {
    if (!this.refreshToken) {
      this.handleLogout();
      return false;
    }
    try {
      const response = await fetch(`${this.supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });
      if (!response.ok) throw new Error('refresh_failed');
      const data = await response.json();
      this.storeSession(data);
      return true;
    } catch (error) {
      // The refresh token itself is invalid/expired/revoked - nothing left
      // to try, back to the login screen.
      this.handleLogout();
      return false;
    }
  }

  handleLogout() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    sessionStorage.removeItem('loubna_admin_token');
    sessionStorage.removeItem('loubna_admin_refresh_token');
    sessionStorage.removeItem('loubna_admin_token_expires_at');
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = 0;
    this.panel.hidden = true;
    this.loginPanel.hidden = false;
  }

  async verifyIsAdmin() {
    try {
      const response = await fetch(`${this.supabaseUrl}/rest/v1/rpc/is_admin`, {
        method: 'POST',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: '{}',
      });
      if (response.status === 401) {
        // The access token may have just expired (e.g. the tab sat in the
        // background long enough for the browser to throttle the refresh
        // timer) - try one silent refresh before concluding the session
        // itself is invalid.
        if (!(await this.refreshSession())) return false;
        const retryResponse = await fetch(`${this.supabaseUrl}/rest/v1/rpc/is_admin`, {
          method: 'POST',
          headers: {
            apikey: this.anonKey,
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: '{}',
        });
        return retryResponse.ok && (await retryResponse.json()) === true;
      }
      if (!response.ok) return false;
      return (await response.json()) === true;
    } catch (error) {
      return false;
    }
  }

  async showDashboard() {
    // A valid Supabase session only proves *someone* is logged in, not that
    // they're an admin - any account (e.g. a writer's own magic-link
    // session, or anyone who can create a password-based account) would
    // otherwise reach this point too. Real data stays safe either way via
    // RLS, but there's no reason to even render the shell for a non-admin.
    const isAdmin = await this.verifyIsAdmin();
    if (!isAdmin) {
      this.handleLogout();
      this.loginError.textContent = 'هذا الحساب لا يملك صلاحية الدخول للوحة التحكم.';
      this.loginError.style.display = 'block';
      return;
    }

    this.loginPanel.hidden = true;
    this.panel.hidden = false;
    await Promise.all([
      this.loadOrders(),
      this.loadProductCosts(),
      this.loadExpenses(),
      this.loadWriterSubmissions(),
      this.loadWriterBooks(),
      this.loadDigitalOrders(),
      this.loadDigitalBooks(),
      this.loadReviews(),
    ]);
    this.renderPromotionTab();
  }

  switchTab(tab) {
    this.tabButtons.forEach((btn) => btn.classList.toggle('is-active', btn.dataset.tab === tab));
    this.tabPanels.forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== tab;
    });
  }

  async loadOrders() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/orders?select=*&order=created_at.desc`,
        {
          headers: {
            apikey: this.anonKey,
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      if (response.status === 401) {
        this.handleLogout();
        return;
      }

      this.orders = await response.json();
      this.render();
      this.renderReport();
    } catch (error) {
      this.emptyEl.hidden = false;
      this.emptyEl.textContent = 'خطأ في تحميل الطلبات.';
    }
  }

  setRange(range) {
    this.currentRange = range;
    this.filterButtons.forEach((btn) =>
      btn.classList.toggle('is-active', btn.dataset.range === range)
    );
    this.render();
  }

  filterByRange(list, dateField, range) {
    if (range === 'all') return list;

    const now = new Date();
    let start;
    if (range === 'today') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (range === 'week') {
      start = new Date(now);
      start.setDate(start.getDate() - 7);
    } else if (range === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    return list.filter((item) => new Date(item[dateField]) >= start);
  }

  getFilteredOrders() {
    return this.filterByRange(this.orders, 'created_at', this.currentRange);
  }

  setReportRange(range) {
    this.reportRange = range;
    this.reportFilterButtons.forEach((btn) =>
      btn.classList.toggle('is-active', btn.dataset.reportRange === range)
    );
    this.renderReport();
  }

  renderReport() {
    const allOrders = this.filterByRange(this.orders, 'created_at', this.reportRange);
    const orders = allOrders.filter((o) => o.status !== 'cancelled' && o.status !== 'returned');
    const returnedCount = allOrders.filter((o) => o.status === 'returned').length;
    const expenses = this.filterByRange(this.expenses, 'expense_date', this.reportRange);

    const revenue = orders.reduce((sum, o) => sum + Number(o.total_price), 0);
    const cogs = orders.reduce((sum, o) => sum + Number(o.cost_of_goods || 0), 0);
    const grossProfit = revenue - cogs;
    const fixedExpenses = expenses
      .filter((e) => e.type === 'fixed')
      .reduce((sum, e) => sum + Number(e.amount), 0);
    const variableExpenses = expenses
      .filter((e) => e.type === 'variable')
      .reduce((sum, e) => sum + Number(e.amount), 0);
    const netProfit = grossProfit - fixedExpenses - variableExpenses;

    const money = (n) => `${n.toFixed(2)} DA`;
    const rows = [
      { label: 'رقم الأعمال (سعر الكتب فقط)', value: revenue },
      { label: 'تكلفة البضاعة المباعة', value: -cogs },
      { label: 'الربح الإجمالي', value: grossProfit, strong: true },
      { label: 'المصاريف المتغيّرة (بما فيها مصاريف المرتجعات)', value: -variableExpenses },
      { label: 'المصاريف الثابتة', value: -fixedExpenses },
      { label: 'الربح الصافي', value: netProfit, highlight: true },
    ];

    this.reportEl.innerHTML = `
      <div class="admin-dashboard__report-rows">
        ${rows
          .map(
            (r) => `
          <div class="admin-dashboard__report-row${r.strong ? ' admin-dashboard__report-row--strong' : ''}${
              r.highlight ? ' admin-dashboard__report-row--highlight' : ''
            }">
            <span>${r.label}</span>
            <span>${money(r.value)}</span>
          </div>`
          )
          .join('')}
      </div>
      <p class="admin-dashboard__report-note">
        عدد المرتجعات خلال هذه الفترة: ${returnedCount} (مستبعدة من رقم الأعمال أعلاه، لأن العميل لم يدفع شيئاً).
        رسوم التوصيل العادية لا تُحسب هنا لأن العميل يدفعها بالكامل، فهي محايدة على الربح. أضف فقط رسوم إرجاع
        الطلبية التي تدفعها أنت في تبويب "المصاريف" (فئة: مصاريف التوصيل) ليُحتسب في الربح الصافي.
      </p>
    `;
  }

  render() {
    const orders = this.getFilteredOrders();
    this.renderStats(orders);
    this.renderTable(orders);
  }

  renderStats(orders) {
    const sum = (list) => list.reduce((total, o) => total + Number(o.grand_total), 0);
    // Cancelled/returned orders never collected any money (the customer
    // either never paid or the parcel was refused), so they'd inflate every
    // money figure here if left in - same exclusion renderReport already
    // applies to its revenue figures, just also needed here since this is a
    // separate sum over the raw (date-filtered only) order list.
    const billable = orders.filter((o) => o.status !== 'cancelled' && o.status !== 'returned');
    const byPayment = (status) => billable.filter((o) => o.payment_status === status);

    const stats = [
      { label: 'عدد الطلبات', value: orders.length },
      { label: 'رقم الأعمال', value: `${sum(billable)} DA`, highlight: true },
      { label: 'بانتظار التحصيل', value: `${sum(byPayment('not_collected'))} DA` },
      { label: 'محصّل عند الموصل', value: `${sum(byPayment('collected'))} DA` },
      { label: 'تم التحويل', value: `${sum(byPayment('remitted'))} DA` },
    ];

    this.statsEl.innerHTML = stats
      .map(
        (s) => `
        <div class="admin-dashboard__stat${s.highlight ? ' admin-dashboard__stat--highlight' : ''}">
          <div class="admin-dashboard__stat-label">${s.label}</div>
          <div class="admin-dashboard__stat-value">${s.value}</div>
        </div>`
      )
      .join('');
  }

  flashSelect(selectEl, className) {
    selectEl.classList.remove('is-saved', 'is-error');
    // Force reflow so the animation restarts if the same class is re-applied
    // in quick succession (e.g. two saves of the same field back to back).
    void selectEl.offsetWidth;
    selectEl.classList.add(className);
    setTimeout(() => selectEl.classList.remove(className), 1500);
  }

  renderTable(orders) {
    this.emptyEl.hidden = orders.length > 0;
    this.tableBody.innerHTML = orders.map((order) => this.renderRow(order)).join('');

    this.tableBody.querySelectorAll('[data-field]').forEach((select) => {
      select.dataset.previousValue = select.value;
      select.addEventListener('change', (event) => {
        const { id, field } = event.target.dataset;
        this.updateOrder(id, field, event.target.value, event.target);
      });
    });
  }

  renderRow(order) {
    const date = new Date(order.created_at).toLocaleDateString('ar-DZ');
    const itemsSummary = (order.items || [])
      .map((item) => `${item.title} ×${item.quantity}`)
      .join('، ');

    return `
      <tr>
        <td>${date}</td>
        <td>${escapeHtml(order.customer_name)}</td>
        <td>${escapeHtml(order.customer_phone)}</td>
        <td>${escapeHtml(order.wilaya)} - ${escapeHtml(order.commune)}</td>
        <td>${escapeHtml(itemsSummary)}</td>
        <td>${order.total_quantity}</td>
        <td>${order.grand_total} DA</td>
        <td>
          <select data-id="${order.id}" data-field="status">
            ${renderOptions(
              {
                pending: 'قيد الانتظار',
                confirmed: 'مؤكدة',
                shipped: 'تم الشحن',
                delivered: 'تم التسليم',
                cancelled: 'ملغاة',
                returned: 'مرتجعة (رفض الاستلام)',
              },
              order.status
            )}
          </select>
        </td>
        <td>
          <select data-id="${order.id}" data-field="payment_status">
            ${renderOptions(
              {
                not_collected: 'بانتظار التحصيل',
                collected: 'محصّل عند الموصل',
                remitted: 'تم التحويل',
              },
              order.payment_status
            )}
          </select>
        </td>
      </tr>`;
  }

  async updateOrder(id, field, value, selectEl) {
    const previousValue = selectEl ? selectEl.dataset.previousValue : null;
    if (selectEl) selectEl.disabled = true;

    let response;
    try {
      if (field === 'status' && (value === 'cancelled' || value === 'returned')) {
        // Setting status to "cancelled" or "returned" isn't just a local
        // label - both also cancel the real Shopify order and restock it,
        // via the cancel-order function (kept separate from create-order's
        // blanket service-role access so this destructive action requires
        // the caller's own authenticated dashboard session). Kept as two
        // distinct statuses so the merchant can still see their return rate.
        const functionsUrl = this.supabaseUrl.replace('.supabase.co', '.supabase.co/functions/v1');
        response = await fetch(`${functionsUrl}/cancel-order`, {
          method: 'POST',
          headers: {
            apikey: this.anonKey,
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ order_id: id, target_status: value }),
        });
      } else {
        response = await fetch(`${this.supabaseUrl}/rest/v1/orders?id=eq.${id}`, {
          method: 'PATCH',
          headers: {
            apikey: this.anonKey,
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
            Prefer: 'return=minimal',
          },
          body: JSON.stringify({ [field]: value }),
        });
      }
    } catch (error) {
      response = null;
    }

    if (selectEl) selectEl.disabled = false;

    if (!response || !response.ok) {
      if (selectEl && previousValue !== null) selectEl.value = previousValue;
      if (selectEl) this.flashSelect(selectEl, 'is-error');
      return;
    }

    if (selectEl) {
      selectEl.dataset.previousValue = value;
      this.flashSelect(selectEl, 'is-saved');
    }

    const order = this.orders.find((o) => o.id === id);
    if (order) order[field] = value;
    this.renderStats(this.getFilteredOrders());
    this.renderReport();
  }

  async loadProductCosts() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/product_costs?select=*&order=product_title.asc`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      this.productCosts = response.ok ? await response.json() : [];
      this.renderCosts();
    } catch (error) {
      this.productCosts = [];
      this.renderCosts();
    }
  }

  renderCosts() {
    this.costsEmpty.hidden = this.productCosts.length > 0;
    this.costsBody.innerHTML = this.productCosts
      .map(
        (c) => `
        <tr>
          <td>${escapeHtml(c.product_title)}</td>
          <td>${Number(c.cost_price).toFixed(2)}</td>
          <td><button type="button" class="admin-dashboard__link" data-delete-cost="${encodeURIComponent(
            c.product_title
          )}">حذف</button></td>
        </tr>`
      )
      .join('');

    this.costsBody.querySelectorAll('[data-delete-cost]').forEach((btn) => {
      btn.addEventListener('click', () => this.deleteCost(decodeURIComponent(btn.dataset.deleteCost)));
    });
  }

  async handleAddCost(event) {
    event.preventDefault();
    const formData = new FormData(this.costsForm);
    const productTitle = formData.get('product_title').trim();
    const costPrice = Number(formData.get('cost_price'));

    await fetch(`${this.supabaseUrl}/rest/v1/product_costs`, {
      method: 'POST',
      headers: {
        apikey: this.anonKey,
        Authorization: `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates,return=minimal',
      },
      body: JSON.stringify({
        product_title: productTitle,
        cost_price: costPrice,
        updated_at: new Date().toISOString(),
      }),
    });

    this.costsForm.reset();
    await this.loadProductCosts();
  }

  async deleteCost(productTitle) {
    await fetch(
      `${this.supabaseUrl}/rest/v1/product_costs?product_title=eq.${encodeURIComponent(productTitle)}`,
      { method: 'DELETE', headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
    );
    await this.loadProductCosts();
  }

  async loadExpenses() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/expenses?select=*&order=expense_date.desc`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      this.expenses = response.ok ? await response.json() : [];
      this.renderExpenses();
      this.renderReport();
    } catch (error) {
      this.expenses = [];
      this.renderExpenses();
    }
  }

  renderExpenses() {
    this.expensesEmpty.hidden = this.expenses.length > 0;
    const typeLabel = { fixed: 'ثابت', variable: 'متغيّر' };
    const categoryLabel = {
      loyer: 'إيجار المحل',
      abonnements: 'اشتراكات',
      publicite: 'إشهار',
      emballage: 'تغليف',
      livraison: 'مصاريف التوصيل (مرتجعات)',
    };

    this.expensesBody.innerHTML = this.expenses
      .map(
        (e) => `
        <tr>
          <td>${new Date(e.expense_date).toLocaleDateString('ar-DZ')}</td>
          <td>${typeLabel[e.type] || escapeHtml(e.type)}</td>
          <td>${categoryLabel[e.category] || escapeHtml(e.category)}</td>
          <td>${escapeHtml(e.description || '')}</td>
          <td>${Number(e.amount).toFixed(2)} DA</td>
          <td><button type="button" class="admin-dashboard__link" data-delete-expense="${e.id}">حذف</button></td>
        </tr>`
      )
      .join('');

    this.expensesBody.querySelectorAll('[data-delete-expense]').forEach((btn) => {
      btn.addEventListener('click', () => this.deleteExpense(btn.dataset.deleteExpense));
    });
  }

  async handleAddExpense(event) {
    event.preventDefault();
    const formData = new FormData(this.expensesForm);
    const payload = {
      expense_date: formData.get('expense_date'),
      type: formData.get('type'),
      category: formData.get('category'),
      description: formData.get('description') || null,
      amount: Number(formData.get('amount')),
    };

    await fetch(`${this.supabaseUrl}/rest/v1/expenses`, {
      method: 'POST',
      headers: {
        apikey: this.anonKey,
        Authorization: `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body: JSON.stringify(payload),
    });

    this.expensesForm.reset();
    await this.loadExpenses();
  }

  async deleteExpense(id) {
    await fetch(`${this.supabaseUrl}/rest/v1/expenses?id=eq.${id}`, {
      method: 'DELETE',
      headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` },
    });
    await this.loadExpenses();
  }

  async loadWriterSubmissions() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/writer_submissions?select=*&order=created_at.desc`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      this.writerSubmissions = response.ok ? await response.json() : [];
      this.renderWriterSubmissions();
    } catch (error) {
      this.writerSubmissions = [];
      this.renderWriterSubmissions();
    }
  }

  renderWriterSubmissions() {
    this.subscriptionsEmpty.hidden = this.writerSubmissions.length > 0;
    this.subscriptionsBody.innerHTML = this.writerSubmissions
      .map((s) => this.renderWriterRow(s))
      .join('');

    this.subscriptionsBody.querySelectorAll('[data-field="writer_status"]').forEach((select) => {
      select.dataset.previousValue = select.value;
      select.addEventListener('change', (event) => {
        this.updateWriterSubmission(event.target.dataset.id, event.target.value, event.target);
      });
    });
  }

  renderWriterRow(submission) {
    const date = new Date(submission.created_at).toLocaleDateString('ar-DZ');
    const tierLabel = { essentiel: 'أساسي', standard: 'قياسي', premium: 'مميز' };
    const regionLabel = { dz: 'الجزائر', intl: 'دولي' };
    const priceUnit = submission.region === 'intl' ? '$' : 'DA';
    return `
      <tr>
        <td>${date}</td>
        <td>${escapeHtml(submission.full_name)}</td>
        <td dir="ltr" style="text-align:right">${escapeHtml(submission.email)}</td>
        <td dir="ltr" style="text-align:right">${escapeHtml(submission.phone || '')}</td>
        <td>${tierLabel[submission.tier] || escapeHtml(submission.tier || '')}</td>
        <td>${regionLabel[submission.region] || escapeHtml(submission.region || '')}</td>
        <td>${submission.subscription_price != null ? submission.subscription_price + ' ' + priceUnit : ''}</td>
        <td>${escapeHtml(submission.book_title || '')}</td>
        <td>
          <select data-id="${submission.id}" data-field="writer_status">
            ${renderOptions(
              {
                pending_payment: 'بانتظار الدفع',
                active: 'نشط',
                rejected: 'مرفوض',
                expired: 'منتهي',
                refunded: 'مسترجع',
              },
              submission.status
            )}
          </select>
          ${
            submission.status === 'active' && submission.refund_eligible_until
              ? `<div class="admin-dashboard__refund-note">ضمان الاسترجاع حتى ${new Date(
                  submission.refund_eligible_until
                ).toLocaleDateString('ar-DZ')}</div>`
              : ''
          }
        </td>
      </tr>`;
  }

  async updateWriterSubmission(id, value, selectEl) {
    const previousValue = selectEl ? selectEl.dataset.previousValue : null;
    if (selectEl) selectEl.disabled = true;

    let response;
    try {
      if (value === 'active') {
        // Activation/renewal: dates + re-publishing any previously
        // unpublished books now happen server-side (admin-reactivate-writer-subscription),
        // so this same action correctly handles both a first activation
        // and a renewal after expiry.
        response = await fetch(`${this.supabaseUrl}/functions/v1/admin-reactivate-writer-subscription`, {
          method: 'POST',
          headers: {
            apikey: this.anonKey,
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ submission_id: id }),
        });
      } else {
        response = await fetch(`${this.supabaseUrl}/rest/v1/writer_submissions?id=eq.${id}`, {
          method: 'PATCH',
          headers: {
            apikey: this.anonKey,
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
            Prefer: 'return=minimal',
          },
          body: JSON.stringify({ status: value }),
        });
      }
    } catch (error) {
      response = null;
    }

    if (selectEl) selectEl.disabled = false;

    if (!response || !response.ok) {
      if (selectEl && previousValue !== null) selectEl.value = previousValue;
      if (selectEl) this.flashSelect(selectEl, 'is-error');
      return;
    }

    if (selectEl) {
      selectEl.dataset.previousValue = value;
      this.flashSelect(selectEl, 'is-saved');
    }

    // Reload so activated_at/renews_at/refund_eligible_until (computed
    // server-side on activation) show up correctly.
    await this.loadWriterSubmissions();
  }

  async loadWriterBooks() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/writer_books?select=*&order=created_at.desc`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      this.writerBooks = response.ok ? await response.json() : [];
      this.renderWriterBooks();
    } catch (error) {
      this.writerBooks = [];
      this.renderWriterBooks();
    }
  }

  renderWriterBooks() {
    this.writerBooksEmpty.hidden = this.writerBooks.length > 0;
    const formatLabel = { print: 'مطبوع', ebook: 'رقمي' };
    const statusLabel = { pending_review: 'قيد المراجعة', published: 'منشور', rejected: 'مرفوض' };
    this.writerBooksBody.innerHTML = this.writerBooks
      .map(
        (b) => `
        <tr>
          <td>${new Date(b.created_at).toLocaleDateString('ar-DZ')}</td>
          <td dir="ltr" style="text-align:right">${escapeHtml(b.writer_email)}</td>
          <td>${escapeHtml(b.book_title)}</td>
          <td>${formatLabel[b.format] || escapeHtml(b.format)}</td>
          <td>${b.is_pack ? '<span class="admin-dashboard__pack-badge">باقة</span>' : '—'}</td>
          <td>${b.price != null ? b.price + ' دج' : '—'}</td>
          <td>${b.format === 'print' ? (b.stock_quantity ?? '—') : 'غير محدود'}</td>
          <td>${escapeHtml(b.genre || '')}</td>
          <td>${escapeHtml(b.description || '')}</td>
          <td>${b.manuscript_path ? 'مرفق' : '—'}</td>
          <td>${b.cover_image_path ? 'مرفق' : '—'}</td>
          <td>${statusLabel[b.status] || escapeHtml(b.status)}</td>
          <td>
            ${
              b.status === 'pending_review'
                ? `
              <div class="admin-dashboard__writer-book-actions">
                <input type="number" min="0" step="0.01" placeholder="سعر البيع (دج)" data-price-for="${b.id}" value="${b.price ?? ''}" style="width:110px">
                <button type="button" class="admin-dashboard__btn" data-approve-book="${b.id}">موافقة</button>
                <button type="button" class="admin-dashboard__link" data-reject-book="${b.id}">رفض</button>
              </div>`
                : '—'
            }
          </td>
        </tr>`
      )
      .join('');

    this.writerBooksBody.querySelectorAll('[data-approve-book]').forEach((btn) => {
      btn.addEventListener('click', () => this.reviewWriterBook(btn.dataset.approveBook, 'approve', btn));
    });
    this.writerBooksBody.querySelectorAll('[data-reject-book]').forEach((btn) => {
      btn.addEventListener('click', () => this.reviewWriterBook(btn.dataset.rejectBook, 'reject', btn));
    });
  }

  async reviewWriterBook(bookId, action, triggerEl) {
    let price;
    if (action === 'approve') {
      const priceInput = this.writerBooksBody.querySelector(`[data-price-for="${bookId}"]`);
      price = Number(priceInput ? priceInput.value : 0);
      if (!(price > 0)) {
        priceInput.focus();
        return;
      }
    }

    const row = triggerEl.closest('tr');
    row.style.opacity = '0.5';

    try {
      const response = await fetch(`${this.supabaseUrl}/functions/v1/admin-review-writer-book`, {
        method: 'POST',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ book_id: bookId, action, price }),
      });
      if (!response.ok) throw new Error('review_failed');
      await this.loadWriterBooks();
      this.renderPromotionTab();
    } catch (error) {
      row.style.opacity = '1';
    }
  }

  // Only Standard/Premium tiers promise homepage rotation and promo
  // stories - Essentiel writers never show up here. Sorted oldest-featured-
  // first (never-featured books sort to the very top) so the merchant can
  // see at a glance whose turn is next, without having to remember herself.
  renderPromotionTab() {
    if (!this.promotionBody) return;
    const tierByEmail = new Map(this.writerSubmissions.map((s) => [s.email, s.tier]));
    const eligible = this.writerBooks
      .filter((b) => b.status === 'published')
      .filter((b) => ['standard', 'premium'].includes(tierByEmail.get(b.writer_email)))
      .sort((a, b) => new Date(a.last_featured_at || 0) - new Date(b.last_featured_at || 0));

    this.promotionEmpty.hidden = eligible.length > 0;
    const tierLabel = { standard: 'قياسي', premium: 'مميز' };

    this.promotionBody.innerHTML = eligible
      .map(
        (b) => `
        <tr>
          <td dir="ltr" style="text-align:right">${escapeHtml(b.writer_email)}</td>
          <td>${escapeHtml(b.book_title)}</td>
          <td>${tierLabel[tierByEmail.get(b.writer_email)] || ''}</td>
          <td>${b.last_featured_at ? new Date(b.last_featured_at).toLocaleDateString('ar-DZ') : 'لم يُعرض بعد'}</td>
          <td>
            <input type="checkbox" data-id="${b.id}" data-field="is_featured" ${b.is_featured ? 'checked' : ''}>
          </td>
          <td>
            <input type="checkbox" data-id="${b.id}" data-field="story_posted" ${b.last_story_posted_at ? 'checked' : ''}>
          </td>
        </tr>`
      )
      .join('');

    this.promotionBody.querySelectorAll('[data-field="is_featured"]').forEach((checkbox) => {
      checkbox.addEventListener('change', (event) => {
        this.toggleBookFeatured(event.target.dataset.id, event.target.checked, event.target);
      });
    });
    this.promotionBody.querySelectorAll('[data-field="story_posted"]').forEach((checkbox) => {
      checkbox.addEventListener('change', (event) => {
        this.markStoryPosted(event.target.dataset.id, event.target.checked, event.target);
      });
    });
  }

  async toggleBookFeatured(bookId, featured, checkboxEl) {
    checkboxEl.disabled = true;
    let response;
    try {
      response = await fetch(`${this.supabaseUrl}/functions/v1/toggle-book-promotion`, {
        method: 'POST',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ book_id: bookId, featured }),
      });
    } catch (error) {
      response = null;
    }
    checkboxEl.disabled = false;

    if (!response || !response.ok) {
      checkboxEl.checked = !featured;
      this.flashSelect(checkboxEl, 'is-error');
      return;
    }

    this.flashSelect(checkboxEl, 'is-saved');
    await this.loadWriterBooks();
    this.renderPromotionTab();
  }

  // Story/reel posting happens off-site on Instagram/TikTok - this is just a
  // manual reminder checkbox for the merchant, a plain field update with no
  // Shopify side effect (unlike is_featured, which also tags the product).
  async markStoryPosted(bookId, posted, checkboxEl) {
    checkboxEl.disabled = true;
    let response;
    try {
      response = await fetch(`${this.supabaseUrl}/rest/v1/writer_books?id=eq.${bookId}`, {
        method: 'PATCH',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal',
        },
        body: JSON.stringify({ last_story_posted_at: posted ? new Date().toISOString() : null }),
      });
    } catch (error) {
      response = null;
    }
    checkboxEl.disabled = false;

    if (!response || !response.ok) {
      checkboxEl.checked = !posted;
      this.flashSelect(checkboxEl, 'is-error');
      return;
    }

    this.flashSelect(checkboxEl, 'is-saved');
    const book = this.writerBooks.find((b) => b.id === bookId);
    if (book) book.last_story_posted_at = posted ? new Date().toISOString() : null;
  }

  async loadDigitalOrders() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/digital_orders?select=*&order=created_at.desc`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      this.digitalOrders = response.ok ? await response.json() : [];
      this.renderDigitalOrders();
    } catch (error) {
      this.digitalOrders = [];
      this.renderDigitalOrders();
    }
  }

  renderDigitalOrders() {
    this.digitalOrdersEmpty.hidden = this.digitalOrders.length > 0;
    this.digitalOrdersBody.innerHTML = this.digitalOrders
      .map((o) => this.renderDigitalOrderRow(o))
      .join('');

    this.digitalOrdersBody.querySelectorAll('[data-field="digital_status"]').forEach((select) => {
      select.dataset.previousValue = select.value;
      select.addEventListener('change', (event) => {
        this.updateDigitalOrder(event.target.dataset.id, event.target.value, event.target);
      });
    });

    this.digitalOrdersBody.querySelectorAll('[data-view-proof]').forEach((btn) => {
      btn.addEventListener('click', () => this.viewPaymentProof(btn.dataset.viewProof));
    });
  }

  renderDigitalOrderRow(order) {
    const date = new Date(order.created_at).toLocaleDateString('ar-DZ');
    const regionLabel = { dz: 'الجزائر', intl: 'دولي' };
    const priceUnit = order.region === 'intl' ? '$' : 'DA';
    return `
      <tr>
        <td>${date}</td>
        <td>${escapeHtml(order.book_title)}</td>
        <td>${order.book_price} ${priceUnit}</td>
        <td>${escapeHtml(order.customer_name)}</td>
        <td dir="ltr" style="text-align:right">${escapeHtml(order.customer_email)}</td>
        <td dir="ltr" style="text-align:right">${escapeHtml(order.customer_phone || '')}</td>
        <td>${regionLabel[order.region] || escapeHtml(order.region || '')}</td>
        <td class="admin-dashboard__cell-wrap">${escapeHtml(order.note || '')}</td>
        <td>
          ${
            order.payment_proof_path
              ? `<button type="button" class="admin-dashboard__link" data-view-proof="${escapeHtml(order.payment_proof_path)}">عرض الإثبات</button>`
              : '—'
          }
        </td>
        <td>
          <select data-id="${order.id}" data-field="digital_status">
            ${renderOptions(
              {
                awaiting_payment: 'بانتظار الدفع',
                payment_submitted: 'تم إرسال إثبات',
                paid: 'مدفوع',
                sent: 'تم الإرسال',
                cancelled: 'ملغى',
              },
              order.status
            )}
          </select>
        </td>
      </tr>`;
  }

  async viewPaymentProof(path) {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/storage/v1/object/authenticated/payment-proofs/${path}`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      if (!response.ok) throw new Error('fetch_failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      alert('تعذر تحميل إثبات الدفع.');
    }
  }

  async updateDigitalOrder(id, value, selectEl) {
    const previousValue = selectEl ? selectEl.dataset.previousValue : null;
    if (selectEl) selectEl.disabled = true;

    let response;
    try {
      response = await fetch(`${this.supabaseUrl}/rest/v1/digital_orders?id=eq.${id}`, {
        method: 'PATCH',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal',
        },
        body: JSON.stringify({ status: value }),
      });
    } catch (error) {
      response = null;
    }

    if (selectEl) selectEl.disabled = false;

    if (!response || !response.ok) {
      if (selectEl && previousValue !== null) selectEl.value = previousValue;
      if (selectEl) this.flashSelect(selectEl, 'is-error');
      return;
    }

    if (selectEl) {
      selectEl.dataset.previousValue = value;
      this.flashSelect(selectEl, 'is-saved');
    }

    const order = this.digitalOrders.find((o) => o.id === id);
    if (order) order.status = value;

    // Marking a manual-proof order 'paid' means the merchant just verified the
    // payment - this is the trigger point to auto-send the ebook, mirroring
    // what stripe-webhook does automatically for card payments.
    if (value === 'paid') {
      try {
        const sendRes = await fetch(`${this.supabaseUrl}/functions/v1/send-ebook-email`, {
          method: 'POST',
          headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
          body: JSON.stringify({ digital_order_id: id }),
        });
        const sendData = await sendRes.json().catch(() => ({}));
        if (sendData.sent && selectEl) {
          selectEl.value = 'sent';
          selectEl.dataset.previousValue = 'sent';
          if (order) order.status = 'sent';
        }
      } catch (error) {
        // Ebook send failure isn't fatal to the status update - the order stays
        // 'paid' and visible for the merchant to follow up manually.
      }
    }
  }

  async loadDigitalBooks() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/digital_books?select=*&order=uploaded_at.desc`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      this.digitalBooks = response.ok ? await response.json() : [];
      this.renderDigitalBooks();
    } catch (error) {
      this.digitalBooks = [];
      this.renderDigitalBooks();
    }
  }

  renderDigitalBooks() {
    this.ebookFilesEmpty.hidden = this.digitalBooks.length > 0;
    this.ebookFilesBody.innerHTML = this.digitalBooks
      .map(
        (b) => `
        <tr>
          <td dir="ltr" style="text-align:right">${escapeHtml(b.book_handle)}</td>
          <td>${new Date(b.uploaded_at).toLocaleDateString('ar-DZ')}</td>
          <td><button type="button" class="admin-dashboard__link" data-delete-ebook-file="${encodeURIComponent(
            b.book_handle
          )}">حذف</button></td>
        </tr>`
      )
      .join('');

    this.ebookFilesBody.querySelectorAll('[data-delete-ebook-file]').forEach((btn) => {
      btn.addEventListener('click', () => this.deleteEbookFile(decodeURIComponent(btn.dataset.deleteEbookFile)));
    });
  }

  async handleUploadEbookFile(event) {
    event.preventDefault();
    const formData = new FormData(this.ebookFilesForm);
    const bookHandle = String(formData.get('book_handle') || '').trim();
    const file = formData.get('file');
    if (!bookHandle || !(file instanceof File) || !file.size) return;

    const submitBtn = this.ebookFilesForm.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    this.ebookFileMessage.style.display = 'none';

    try {
      const fileBase64 = await fileToBase64(file);

      const uploadRes = await fetch(`${this.supabaseUrl}/functions/v1/admin-upload-ebook-file`, {
        method: 'POST',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          book_handle: bookHandle,
          file_base64: fileBase64,
          file_name: file.name,
          content_type: file.type || 'application/pdf',
        }),
      });
      if (!uploadRes.ok) throw new Error('upload_failed');

      this.ebookFilesForm.reset();
      this.showEbookFileMessage(true, 'تم رفع الملف بنجاح.');
      await this.loadDigitalBooks();
    } catch (error) {
      this.showEbookFileMessage(false, 'تعذر رفع الملف، حاول مجدداً.');
    } finally {
      submitBtn.disabled = false;
    }
  }

  showEbookFileMessage(success, text) {
    this.ebookFileMessage.textContent = text;
    this.ebookFileMessage.style.color = success ? '#173e33' : '#b22222';
    this.ebookFileMessage.style.display = 'block';
  }

  async deleteEbookFile(bookHandle) {
    await fetch(
      `${this.supabaseUrl}/rest/v1/digital_books?book_handle=eq.${encodeURIComponent(bookHandle)}`,
      { method: 'DELETE', headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
    );
    await this.loadDigitalBooks();
  }

  exportCsv() {
    const orders = this.getFilteredOrders();
    const headers = [
      'date',
      'nom',
      'telephone',
      'wilaya',
      'commune',
      'articles',
      'quantite',
      'mode_livraison',
      'total',
      'statut',
      'paiement',
    ];

    const rows = orders.map((o) => [
      new Date(o.created_at).toLocaleString('fr-FR'),
      o.customer_name,
      o.customer_phone,
      o.wilaya,
      o.commune,
      (o.items || []).map((i) => `${i.title} x${i.quantity}`).join(' | '),
      o.total_quantity,
      o.delivery_mode,
      o.grand_total,
      o.status,
      o.payment_status,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `commandes-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async loadReviews() {
    try {
      const response = await fetch(
        `${this.supabaseUrl}/rest/v1/customer_reviews?select=*&order=created_at.desc`,
        { headers: { apikey: this.anonKey, Authorization: `Bearer ${this.accessToken}` } }
      );
      this.reviews = response.ok ? await response.json() : [];
      this.renderReviews();
    } catch (error) {
      this.reviews = [];
      this.renderReviews();
    }
  }

  renderReviews() {
    this.reviewsEmpty.hidden = this.reviews.length > 0;
    this.reviewsBody.innerHTML = this.reviews.map((rv) => this.renderReviewRow(rv)).join('');

    this.reviewsBody.querySelectorAll('[data-field="review_status"]').forEach((select) => {
      select.dataset.previousValue = select.value;
      select.addEventListener('change', (event) => {
        this.updateReview(event.target.dataset.id, event.target.value, event.target);
      });
    });
  }

  renderReviewRow(review) {
    const date = new Date(review.created_at).toLocaleDateString('ar-DZ');
    const stars = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
    return `
      <tr>
        <td>${date}</td>
        <td>${escapeHtml(review.customer_name)}</td>
        <td>${escapeHtml(review.book_title || '')}</td>
        <td>${stars}</td>
        <td class="admin-dashboard__cell-wrap">${escapeHtml(review.review_text)}</td>
        <td>
          <select data-id="${review.id}" data-field="review_status">
            ${renderOptions(
              {
                pending: 'قيد المراجعة',
                approved: 'موافق عليه',
                rejected: 'مرفوض',
              },
              review.status
            )}
          </select>
        </td>
      </tr>`;
  }

  async updateReview(id, value, selectEl) {
    const previousValue = selectEl ? selectEl.dataset.previousValue : null;
    if (selectEl) selectEl.disabled = true;

    const patch = { status: value };
    if (value === 'approved') patch.approved_at = new Date().toISOString();

    let response;
    try {
      response = await fetch(`${this.supabaseUrl}/rest/v1/customer_reviews?id=eq.${id}`, {
        method: 'PATCH',
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal',
        },
        body: JSON.stringify(patch),
      });
    } catch (error) {
      response = null;
    }

    if (selectEl) selectEl.disabled = false;

    if (!response || !response.ok) {
      if (selectEl && previousValue !== null) selectEl.value = previousValue;
      if (selectEl) this.flashSelect(selectEl, 'is-error');
      return;
    }

    if (selectEl) {
      selectEl.dataset.previousValue = value;
      this.flashSelect(selectEl, 'is-saved');
    }

    const review = this.reviews.find((r) => r.id === id);
    if (review) review.status = value;
  }
}

function renderOptions(map, selected) {
  return Object.entries(map)
    .map(([value, label]) => `<option value="${value}"${value === selected ? ' selected' : ''}>${label}</option>`)
    .join('');
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

customElements.define('admin-dashboard', AdminDashboard);
